import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import dokumenteService, { DokumentEntry } from '../../services/dokumenteService';
import { resolveAssetUrl } from '../../utils/apiConfig';

type SnackbarState = {
  message: string;
  severity: 'success' | 'error' | 'info';
} | null;

type SortOption = 'title' | 'filename' | 'date';

type EditDialogState = {
  open: boolean;
  documentId: string | null;
  title: string;
  description: string;
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { token, canManageDocuments, canViewDocuments, canUploadPdfs, canEditDocuments, canDeleteDocuments } = useAuth();

  const [documents, setDocuments] = useState<DokumentEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('title');
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [editDialog, setEditDialog] = useState<EditDialogState>({
    open: false,
    documentId: null,
    title: '',
    description: ''
  });

  // Lade Dokumente beim Mount
  useEffect(() => {
    let active = true;

    const fetchDocuments = async () => {
      if (!token || !canViewDocuments) return;

      setLoading(true);
      try {
        const response = await dokumenteService.getDocuments(token);
        if (!active) return;

        setDocuments(response.documents);
        setLastUpdated(response.updatedAt);
      } catch (error) {
        if (!active) return;
        setSnackbar({
          message: error instanceof Error ? error.message : 'Fehler beim Laden der Dokumente',
          severity: 'error'
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchDocuments();

    return () => {
      active = false;
    };
  }, [token, canViewDocuments]);

  // Filtere und sortiere Dokumente
  const filtered = useMemo(() => {
    const searched = dokumenteService.searchDocuments(documents, searchQuery);
    return dokumenteService.sortDocuments(searched, sortBy);
  }, [documents, searchQuery, sortBy]);

  if (!canViewDocuments) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer Dokumente.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  // Handle PDF Upload
  const handleUploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    const canUpload = canManageDocuments || canUploadPdfs;
    if (!file || !canUpload || !token) {
      setSnackbar({
        message: 'Keine Datei ausgewählt oder keine Admin-Berechtigung',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    try {
      const uploaded = await dokumenteService.uploadDocument(
        file,
        file.name.replace('.pdf', '').replace(/[_-]/g, ' '),
        '',
        token
      );

      setDocuments(prev => [...prev, uploaded]);
      setSnackbar({
        message: `${uploaded.filename} erfolgreich hochgeladen`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        message: error instanceof Error ? error.message : 'Upload fehlgeschlagen',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle Bearbeitung öffnen
  const handleOpenEdit = (doc: DokumentEntry) => {
    setEditDialog({
      open: true,
      documentId: doc.id,
      title: doc.title,
      description: doc.description
    });
  };

  // Handle Bearbeitung speichern
  const handleSaveEdit = async () => {
    if (!editDialog.documentId || !token || !canEditDocuments) return;

    try {
      const updated = await dokumenteService.updateDocument(
        editDialog.documentId,
        editDialog.title,
        editDialog.description,
        token
      );

      // Aktualisiere lokale Liste und lastUpdated
      setDocuments(prev => prev.map(doc => (doc.id === updated.id ? updated : doc)));
      setLastUpdated(Date.now());

      setEditDialog({ open: false, documentId: null, title: '', description: '' });
      setSnackbar({
        message: 'Dokument aktualisiert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        message: error instanceof Error ? error.message : 'Aktualisierung fehlgeschlagen',
        severity: 'error'
      });
    }
  };

  // Handle Löschen
  const handleDelete = async (doc: DokumentEntry) => {
    if (!token || !canDeleteDocuments) return;

    if (!window.confirm(`Möchtest du "${doc.title || doc.filename}" wirklich löschen?`)) {
      return;
    }

    try {
      await dokumenteService.deleteDocument(doc.id, token);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setSnackbar({
        message: `${doc.filename} gelöscht`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        message: error instanceof Error ? error.message : 'Löschen fehlgeschlagen',
        severity: 'error'
      });
    }
  };

  // Handle Download
  const handleDownload = (doc: DokumentEntry) => {
    const downloadUrl = resolveAssetUrl(`/Dokumentation/${doc.filename}`) || `/Dokumentation/${doc.filename}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = doc.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({
      message: `"${doc.title || doc.filename}" wird heruntergeladen`,
      severity: 'success'
    });
  };

  const cardBgColor = darkMode ? 'rgba(50,65,85,0.7)' : 'rgba(255,255,255,0.94)';
  const textColor = darkMode ? tokens.colors.dark.text : '#0D3559';

  return (
    <UnitPageShell title="Dokumentation" showTTS={false}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>

        {/* Admin Panel */}
        {(canManageDocuments || canUploadPdfs) && (
          <Paper elevation={6} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, backgroundColor: cardBgColor, mb: 3 }}>
            <Stack spacing={2.5}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textColor }}>
                Admin-Bereich
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{ flex: { md: 1 } }}
                >
                  PDF hochladen
                  <input
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={handleUploadFile}
                  />
                </Button>

                {uploading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ color: textColor }}>
                      Datei wird hochgeladen …
                    </Typography>
                  </Stack>
                )}

                {!uploading && lastUpdated && (
                  <Typography variant="body2" sx={{ color: textColor, alignSelf: 'center' }}>
                    Letzte Änderung: {new Date(lastUpdated * 1000).toLocaleString('de-DE')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Suchleiste & Sortierung */}
        <Paper elevation={4} sx={{ p: 2, borderRadius: 2, backgroundColor: cardBgColor, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              fullWidth
              placeholder="Suche nach Dateiname, Titel oder Beschreibung …"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: textColor }} />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <SortIcon sx={{ color: textColor }} />
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="title">Nach Titel</MenuItem>
                <MenuItem value="filename">Nach Dateiname</MenuItem>
                <MenuItem value="date">Nach Datum</MenuItem>
              </Select>
            </Stack>
          </Stack>
        </Paper>

        {/* Dokumentenliste */}
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2, color: textColor }}>
              Dokumente werden geladen …
            </Typography>
          </Stack>
        ) : filtered.length === 0 ? (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, backgroundColor: cardBgColor, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: textColor }}>
              {documents.length === 0
                ? 'Noch keine Dokumente vorhanden'
                : 'Keine Dokumente entsprechen der Suchanfrage'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={4} sx={{ borderRadius: 2, backgroundColor: cardBgColor }}>
            <Table>
              <TableHead sx={{ backgroundColor: darkMode ? 'rgba(70,90,110,0.5)' : 'rgba(13,53,89,0.08)' }}>
                <TableRow>
                  <TableCell sx={{ color: textColor, fontWeight: 700 }}>Titel</TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 700 }}>Beschreibung</TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 700 }}>Dateiname</TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 700 }}>Größe</TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 700 }} align="right">
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((doc) => (
                  <TableRow key={doc.id} sx={{ '&:hover': { backgroundColor: darkMode ? 'rgba(100,120,140,0.2)' : 'rgba(13,53,89,0.05)' } }}>
                    <TableCell sx={{ color: textColor }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doc.title || '(Ohne Titel)'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: textColor }}>
                      <Typography variant="caption">{doc.description || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: textColor }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {doc.filename}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: textColor }}>
                      <Typography variant="caption">
                        {(doc.filesize / 1024 / 1024).toFixed(1)} MB
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Herunterladen">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc)}
                            sx={{ color: '#1976d2' }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {canEditDocuments && (
                            <Tooltip title="Bearbeiten">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(doc)}
                                sx={{ color: '#FF9800' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        )}
                        {canDeleteDocuments && (
                            <Tooltip title="Löschen">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(doc)}
                                sx={{ color: '#B2140F' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ ...editDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Dokument bearbeiten</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Titel"
              value={editDialog.title}
              onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Beschreibung"
              value={editDialog.description}
              onChange={(e) => setEditDialog({ ...editDialog, description: e.target.value })}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ ...editDialog, open: false })}>Abbrechen</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </UnitPageShell>
  );
};

export default Page;
